import { applicableRecipes, checkFormLayout, DEFAULT_LAYOUT_ID, layoutOf, blockComponent, type LayoutFinding } from '@ayumia/component-runtime';
import type { ComponentManifest, DesignRecipeBody, InspectionTemplateBody, LayoutBody } from '@ayumia/contracts';

/**
 * Registres que l'éditeur 1 lit (ADR-146 §3) : gabarits de mise en page (`layout_registry`), blocs
 * (`component_registry` : composants qui déclarent `block`) et recettes de design. Les recettes ne sont
 * jamais montrées (§3.4) : l'éditeur les applique à l'insertion, avec le même calcul que le serveur à la
 * publication (`checkFormLayout`, un seul calcul) ; le motif d'un refus est celui de la recette.
 */

type Template = InspectionTemplateBody;
type Localized = Readonly<Record<string, string>>;

export interface BlockDeclaration {
  category: string;
  input: string;
  variants?: string[];
  sizes?: string[];
  exposed_props?: Array<{ name: string; type: string; label: Localized }>;
  bindings?: Array<{ name: string; expects: string; label: Localized; required?: boolean }>;
}

export interface RegistryLayout {
  artifact_id: string;
  origin: string;
  body: LayoutBody;
}

export interface RegistryComponent {
  /** Nom du registre Core (`ObjectBanner`) ou `artifact_id` d'un composite. */
  name: string;
  version: string;
  origin: string;
  manifest: ComponentManifest;
}

export interface RegistryRecipe {
  artifact_id: string;
  body: DesignRecipeBody;
}

export interface EditorRegistry {
  layouts: readonly RegistryLayout[];
  components: readonly RegistryComponent[];
  recipes: readonly RegistryRecipe[];
}

export const blockOf = (component: RegistryComponent | undefined): BlockDeclaration | undefined => (component?.manifest as { block?: BlockDeclaration } | undefined)?.block;

/** Le bloc a-t-il un rendu sur la tablette (Flutter) ? Sans rendu : « non pris en charge sur la tablette ». */
export function flutterSupported(component: RegistryComponent): boolean {
  const runtimes = (component.manifest as { implementation?: { runtimes?: Record<string, unknown> } }).implementation?.runtimes;
  return runtimes?.['flutter'] !== undefined;
}

export const fr = (label: unknown, fallback = ''): string => {
  if (label === null || typeof label !== 'object') return fallback;
  const record = label as Record<string, string>;
  return record['fr'] ?? Object.values(record)[0] ?? fallback;
};

export function layoutById(registry: EditorRegistry, id: string | undefined): RegistryLayout | undefined {
  return registry.layouts.find((layout) => layout.artifact_id === (id ?? DEFAULT_LAYOUT_ID));
}

export function componentByName(registry: EditorRegistry, name: string): RegistryComponent | undefined {
  return registry.components.find((component) => component.name === name);
}

/** Gabarit de mise en page du formulaire (celui par défaut s'il n'en cite pas). */
export function currentLayout(registry: EditorRegistry, template: Template): RegistryLayout | undefined {
  return layoutById(registry, layoutOf(template)?.layout_ref);
}

/** Recettes qui s'appliquent au formulaire : le calcul du Core, le même qu'à la publication. Jamais affichées. */
export function recipesFor(registry: EditorRegistry, layout: RegistryLayout | undefined): RegistryRecipe[] {
  return applicableRecipes(registry.recipes, layout?.body);
}

/** Constats de la couche de mise en page, calculés dans le navigateur avec le calcul du serveur. */
export function layoutFindings(registry: EditorRegistry, template: Template): LayoutFinding[] {
  if (layoutOf(template) === undefined) return [];
  const layout = currentLayout(registry, template);
  const components = new Map<string, ComponentManifest | null>();
  for (const block of layoutOf(template)?.blocks ?? []) {
    const { name } = blockComponent(block);
    components.set(name, componentByName(registry, name)?.manifest ?? null);
  }
  return checkFormLayout(template, { layout: layout === undefined ? null : { artifact_id: layout.artifact_id, body: layout.body }, components, recipes: recipesFor(registry, layout) });
}

/** Codes qui refusent une insertion ou un choix : la recette du poste et les règles des régions. */
const REFUSING = new Set([
  'RECIPE_BLOCK_CATEGORY_FORBIDDEN',
  'RECIPE_BLOCK_INPUT_FORBIDDEN',
  'RECIPE_NAVIGATION_FORBIDDEN',
  'RECIPE_NAVIGATION_FORBIDDEN_FOR_INTENT',
  'LAYOUT_REGION_REFUSES_CATEGORY',
  'LAYOUT_REGION_FULL',
  'LAYOUT_NAVIGATION_NOT_ALLOWED',
]);

// Par code et message (qui nomme l'élément), pas par chemin : une insertion décale les indices des blocs suivants.
const keyOf = (finding: LayoutFinding): string => `${finding.code}|${finding.message}`;

/**
 * Refus d'un changement (insertion d'un bloc, choix d'une navigation, déplacement) : le premier constat
 * bloquant que le changement ajoute, avec son message en langage courant ; `null` s'il est admis. Un
 * constat déjà présent avant le changement ne le refuse pas (on ne bloque pas l'auteur sur l'existant).
 */
export function refusalOf(registry: EditorRegistry, before: Template, after: Template): string | null {
  const known = new Set(layoutFindings(registry, before).filter((finding) => finding.severity === 'error').map(keyOf));
  const added = layoutFindings(registry, after).find((finding) => finding.severity === 'error' && REFUSING.has(finding.code) && !known.has(keyOf(finding)));
  return added?.message ?? null;
}
